import { getPostAuthLandingPath } from '../../lib/resumeOnlyMode'
import { reportRiskSignal } from '../../lib/risk/reportRiskSignal'
import { assertStagingEmailAllowed, STAGING_ACCESS_DENIED_MESSAGE } from '../../lib/stagingAccess'
import { supabase } from '../../lib/supabase'

import {
  isNetworkError,
  isEmailConfirmationError,
  isInvalidCredentialsError,
  getNetworkErrorMessage,
  getEmailConfirmationErrorMessage,
  getInvalidCredentialsErrorMessage,
  getDefaultErrorMessage,
  validatePassword,
  isUserAlreadyRegistered,
  isDuplicateSignupUser,
  getUserAlreadyRegisteredErrorMessage,
  getNetworkErrorMessageForSignup,
  getDefaultSignupErrorMessage
} from './helpers'
import { AuthMode } from './types'

// Helper function to get login error message for invalid credentials
export function getLoginErrorMessageForInvalidCredentials(signInError: { message: string; status?: number }): string {
  if (isEmailConfirmationError(signInError.message)) {
    return getEmailConfirmationErrorMessage()
  }
  return getInvalidCredentialsErrorMessage()
}

// Helper function to get login error message
export function getLoginErrorMessage(signInError: { message: string; status?: number }): string {
  if (isNetworkError(signInError.message)) {
    return getNetworkErrorMessage()
  }
  
  if (isInvalidCredentialsError(signInError)) {
    return getLoginErrorMessageForInvalidCredentials(signInError)
  }
  
  return getDefaultErrorMessage(signInError.message)
}

// Helper function to check if email is not confirmed
export function isEmailNotConfirmed(user: { email_confirmed_at: string | null; app_metadata?: { provider?: string } }): boolean {
  return !user.email_confirmed_at && user.app_metadata?.provider === 'email'
}

// Helper function to handle email not confirmed
export async function handleEmailNotConfirmed(
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  console.warn('[Login] Email not confirmed, signing out')
  await supabase.auth.signOut()
  setError('Please confirm your email address before signing in. Check your email for the confirmation link.')
}

// Helper function to check email confirmation
export async function checkEmailConfirmation(
  user: { email_confirmed_at: string | null; app_metadata?: { provider?: string } },
  setError: React.Dispatch<React.SetStateAction<string | null>>
): Promise<boolean> {
  if (isEmailNotConfirmed(user)) {
    await handleEmailNotConfirmed(setError)
    return false
  }
  return true
}

// Helper function to handle sign in error
export function handleSignInError(
  signInError: { message: string; status?: number; name?: string },
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  setLoading(false)
  console.error('[Login] Sign in error:', {
    message: signInError.message,
    status: signInError.status,
    name: signInError.name,
    error: signInError,
  })
  setError(getLoginErrorMessage(signInError))
}

// Helper function to handle missing user data
export function handleMissingUserData(
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  setLoading(false)
  console.error('[Login] No user data returned from signInWithPassword')
  setError('Sign in failed. No user data returned. Please try again.')
}

// Helper function to check if email is confirmed
export async function checkIfEmailConfirmed(
  user: { email_confirmed_at: string | null; app_metadata?: { provider?: string } },
  setError: React.Dispatch<React.SetStateAction<string | null>>
): Promise<boolean> {
  return await checkEmailConfirmation(user, setError)
}

// Helper function to handle email not confirmed during login
export function handleEmailNotConfirmedDuringLogin(
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  setLoading(false)
  setError('Please confirm your email address before signing in. Check your email for the confirmation link.')
}

// Helper function to complete successful login
export async function completeSuccessfulLogin(
  userId: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  navigate: (path: string) => void
) {
  const { data: onboardingData } = await supabase
    .from('user_onboarding')
    .select('completed_at')
    .eq('user_id', userId)
    .maybeSingle()

  const landingPath = getPostAuthLandingPath({
    onboardingComplete: !!onboardingData?.completed_at,
  })
  console.log('[Login] Sign in successful, navigating to', landingPath)
  setLoading(false)
  navigate(landingPath)
}

// Helper function to handle successful login
export async function handleSuccessfulLogin(
  user: { id: string; email_confirmed_at: string | null; app_metadata?: { provider?: string } },
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  navigate: (path: string) => void
) {
  const isEmailConfirmed = await checkIfEmailConfirmed(user, setError)
  if (!isEmailConfirmed) {
    handleEmailNotConfirmedDuringLogin(setLoading, setError)
    return
  }

  await completeSuccessfulLogin(user.id, setLoading, navigate)
}

// Helper function to log sign in attempt
export function logSignInAttempt(email: string) {
  console.log('[Login] Attempting to sign in with email:', email)
}

// Helper function to perform sign in
export async function performSignIn(email: string, password: string) {
  assertStagingEmailAllowed(email)
  return await supabase.auth.signInWithPassword({
    email,
    password,
  })
}

// Helper function to log sign in response
export function logSignInResponse(signInData: { user: unknown } | null, signInError: { message: string } | null) {
  console.log('[Login] Sign in response:', {
    hasError: !!signInError,
    error: signInError,
    hasData: !!signInData,
    user: signInData?.user ? {
      id: (signInData.user as { id: string; email: string; email_confirmed_at: string | null }).id,
      email: (signInData.user as { id: string; email: string; email_confirmed_at: string | null }).email,
      email_confirmed_at: (signInData.user as { id: string; email: string; email_confirmed_at: string | null }).email_confirmed_at,
    } : null,
  })
}

// Helper function to log user data
export function logUserData(user: { id: string; email: string; email_confirmed_at: string | null; app_metadata?: { provider?: string } }) {
  console.log('[Login] User data:', {
    id: user.id,
    email: user.email,
    email_confirmed_at: user.email_confirmed_at,
    app_metadata: user.app_metadata,
  })
}

// Helper function to handle unexpected error
export function handleUnexpectedError(
  err: unknown,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  setLoading(false)
  const errorMessage = err instanceof Error ? err.message : String(err)
  console.error('[Login] Unexpected error:', errorMessage)
  setError(errorMessage || 'An unexpected error occurred. Please try again.')
}

// Helper function to check if sign in has error
export function hasSignInError(signInError: { message: string; status?: number } | null): boolean {
  return !!signInError
}

// Helper function to check if sign in has user
export function hasSignInUser(signInData: { user: { id: string; email: string; email_confirmed_at: string | null; app_metadata?: { provider?: string } } } | null): boolean {
  return !!signInData?.user
}

// Helper function to process successful sign in
export async function processSuccessfulSignIn(
  user: { id: string; email: string; email_confirmed_at: string | null; app_metadata?: { provider?: string } },
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  navigate: (path: string) => void
) {
  logUserData(user)
  try {
    assertStagingEmailAllowed(user.email)
  } catch (err) {
    await supabase.auth.signOut()
    setLoading(false)
    setError(err instanceof Error ? err.message : STAGING_ACCESS_DENIED_MESSAGE)
    return
  }
  await handleSuccessfulLogin(user, setLoading, setError, navigate)
  reportRiskSignal('login')
}

// Helper function to process sign in response
export async function processSignInResponse(
  signInData: { user: { id: string; email: string; email_confirmed_at: string | null; app_metadata?: { provider?: string } } } | null,
  signInError: { message: string; status?: number } | null,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  navigate: (path: string) => void
) {
  if (hasSignInError(signInError)) {
    handleSignInError(signInError!, setLoading, setError)
    return
  }

  if (!hasSignInUser(signInData)) {
    handleMissingUserData(setLoading, setError)
    return
  }

  const user = signInData!.user
  await processSuccessfulSignIn(user, setLoading, setError, navigate)
}

// Helper function to get signup error message
export function getSignupErrorMessage(signUpError: { message: string; status?: number }): string {
  const errorMessage = signUpError.message || ''
  
  if (isUserAlreadyRegistered(errorMessage, signUpError.status)) {
    return getUserAlreadyRegisteredErrorMessage()
  }
  
  if (isNetworkError(errorMessage)) {
    return getNetworkErrorMessageForSignup()
  }
  
  return getDefaultSignupErrorMessage(errorMessage)
}

// Helper function to validate password for signup
export function validatePasswordForSignup(
  password: string,
  setError: React.Dispatch<React.SetStateAction<string | null>>
): boolean {
  const passwordValidation = validatePassword(password)
  if (!passwordValidation.valid) {
    setError(passwordValidation.error || 'Invalid password.')
    return false
  }
  return true
}

// Helper function to check if passwords match
export function checkPasswordsMatch(
  password: string,
  confirmPassword: string,
  setError: React.Dispatch<React.SetStateAction<string | null>>
): boolean {
  if (password !== confirmPassword) {
    setError('Passwords do not match.')
    return false
  }
  return true
}

// Helper function to handle sign up error
export function handleSignUpError(
  signUpError: { message: string; status?: number },
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  console.log('[SignUp] Supabase error:', {
    message: signUpError.message,
    status: signUpError.status,
    error: signUpError
  })
  setError(getSignupErrorMessage(signUpError))
}

// Helper function to handle successful signup
export async function handleSuccessfulSignup(
  user: { id: string; email?: string; email_confirmed_at: string | null; created_at: string },
  email: string,
  navigate: (path: string, options?: { replace?: boolean }) => void
) {
  console.log('[SignUp] User created successfully:', {
    id: user.id,
    email: user.email,
    email_confirmed_at: user.email_confirmed_at,
    created_at: user.created_at
  })

  if (!user.email_confirmed_at) {
    await supabase.auth.signOut()
  }
  
  console.log('[SignUp] User registered, email will be sent via Auth Hook')
  navigate(`/auth/signup-success?email=${encodeURIComponent(email)}`, { replace: true })
}

export function isSignupResponseDuplicate(user: { identities?: { length: number }[] | null } | null): boolean {
  return isDuplicateSignupUser(user)
}

export async function processSignUpResponse(
  data: { user: { id: string; email?: string; email_confirmed_at: string | null; created_at: string; identities?: { length: number }[] | null } | null } | null,
  signUpError: { message: string; status?: number } | null,
  email: string,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  navigate: (path: string, options?: { replace?: boolean }) => void
): Promise<void> {
  if (signUpError) {
    handleSignUpError(signUpError, setError)
    return
  }

  if (isSignupResponseDuplicate(data?.user ?? null)) {
    handleSignUpError({ message: 'User already registered', status: 422 }, setError)
    return
  }

  if (data?.user) {
    reportRiskSignal('signup')
    await handleSuccessfulSignup(data.user, email, navigate)
  }
}

// Helper function to handle OAuth error
export function handleOAuthError(
  oauthError: { message: string },
  provider: 'google' | 'linkedin',
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) {
  setError(oauthError.message || `Failed to sign in with ${provider}. Please try again.`)
  setLoading(false)
}

// Helper function to perform OAuth sign in
export async function performOAuthSignIn(provider: 'google' | 'linkedin') {
  return await supabase.auth.signInWithOAuth({
    provider,
    options: {
      // For OAuth, redirect directly to onboarding since email is already confirmed
      // RequireAuth will handle the redirect to onboarding if needed
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  })
}

// Helper function to check if OAuth has error
export function hasOAuthError(oauthError: { message: string } | null): boolean {
  return !!oauthError
}

// Helper function to handle OAuth unexpected error
export function handleOAuthUnexpectedError(
  err: unknown,
  provider: 'google' | 'linkedin',
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  setLoading(false)
  const errorMessage = err instanceof Error ? err.message : String(err)
  setError(errorMessage || `An unexpected error occurred during ${provider} sign in. Please try again.`)
}

// Helper function to reset form state
export function resetFormState(
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setPassword: React.Dispatch<React.SetStateAction<string>>,
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>,
  setPasswordFocused: React.Dispatch<React.SetStateAction<boolean>>
) {
  setError(null)
  setPassword('')
  setConfirmPassword('')
  setPasswordFocused(false)
}

// Helper function to handle mode switch to login
export function handleModeSwitchToLogin(
  setMode: React.Dispatch<React.SetStateAction<AuthMode>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setPassword: React.Dispatch<React.SetStateAction<string>>,
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>,
  setPasswordFocused: React.Dispatch<React.SetStateAction<boolean>>
) {
  setMode('login')
  resetFormState(setError, setPassword, setConfirmPassword, setPasswordFocused)
}

// Helper function to handle mode switch to signup
export function handleModeSwitchToSignup(
  setMode: React.Dispatch<React.SetStateAction<AuthMode>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setPassword: React.Dispatch<React.SetStateAction<string>>,
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>,
  setPasswordFocused: React.Dispatch<React.SetStateAction<boolean>>
) {
  setMode('signup')
  resetFormState(setError, setPassword, setConfirmPassword, setPasswordFocused)
}

// Helper function to get login tab classes
export function getLoginTabClasses(mode: AuthMode): string {
  const base = 'border-b-2 -mb-px transition-colors duration-fast'
  return mode === 'login'
    ? `${base} border-brand-primary text-brand-primary font-semibold`
    : `${base} border-transparent text-text-secondary hover:text-text-primary font-medium`
}

// Helper function to get signup tab classes
export function getSignupTabClasses(mode: AuthMode): string {
  const base = 'border-b-2 -mb-px transition-colors duration-fast'
  return mode === 'signup'
    ? `${base} border-brand-primary text-brand-primary font-semibold`
    : `${base} border-transparent text-text-secondary hover:text-text-primary font-medium`
}
